import type { Formula } from "building-types/options/formula";
import { formulaNew } from "./formulaNew";
import { isFormula, parseFormula } from "./formulaParsing";

export const checkFormula = (formula: string): { ok: boolean; errorMessage?: string } => {
  try {
    parseFormula(formula);
    return {
      ok: true,
    };
  } catch (e) {
    return {
      ok: false,
      errorMessage: e.message,
    };
  }
};

export function assertIsNotFormula<T>(slope: T | Formula): asserts slope is T {
  if (isFormula(slope)) {
    throw new Error("Value is a formula");
  }
}

export function formulasEqual(a: Formula, b: Formula): boolean {
  // If either is missing, not equal

  if (a === undefined && b === undefined) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  try {
    // Compare canonical forms produced by the parser to ignore insignificant formatting differences
    const aParsed = parseFormula(a.formula);
    const bParsed = parseFormula(b.formula);
    return aParsed.toString() === bParsed.toString();
  } catch (_e) {
    // Fallback to strict string comparison if parsing fails
    return a?.formula === b?.formula;
  }
}

export function formulaInvert(value: any): any {
  if (isFormula(value)) {
    if (value.formula && value.formula.trim().startsWith("not (")) {
      return formulaNew(value.formula.trim().slice(5, -1));
    } else {
      return formulaNew(`not (${value.formula.trim()})`);
    }
  } else {
    return value;
  }
}

export const DEFAULT_IDENTIFIER_PREFIX = "identifier_";

/**
 * Rewrites a formula by replacing old ids with new ones. Option set ids appear as identifiers and
 * option ids as quoted values, so a plain substring swap covers both. Returns the original formula
 * when nothing changed.
 */
export function formulaRemapIds(formula: Formula, idRemap: Record<string, string>): Formula {
  let remapped = formula.formula;
  for (const [oldId, newId] of Object.entries(idRemap)) {
    if (oldId !== newId) {
      remapped = remapped.split(oldId).join(newId);
    }
  }
  return remapped === formula.formula ? formula : formulaNew(remapped);
}
